/* eslint-disable simple-import-sort/imports */
import clsx from 'clsx';
import { useContext } from 'react';
import { Trans } from '@/components/tools/translation';

// Types
import type { KPIDataType } from '@/lib/analytics/kpis/kpi.type';

// Hooks
import useFormatKpiValue, {
    KPI_VALUE_MEDIA_VIEWER_CLASSIC,
    KPI_VALUE_CLASSIC,
    KPI_VALUE_DATE,
    KPI_VALUE_DAY_OF_WEEK,
    KPI_VALUE_TIME,
    KPI_VALUE_TIME_DECIMAL,
    KPI_VALUE_PERCENT,
    KPI_VALUE_MEDIA_VIEWER_PERCENT
} from '@/utils/hooks/format/useFormatKpiValue';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';

// Components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import { CompoundTableContext } from '@/components/base/Table/CompoundTable';

type IconEvolutionProps = Pick<KpiCellProps, 'data'> & { formatData: (arg: number) => string };

const IconEvolution = ({ data, formatData }: IconEvolutionProps) => {
    const { table } = useContext(CompoundTableContext);
    const timezone = table.options.meta.timezone;
    const dateFormat = table.options.meta.date_format;
    const evolution = data.evolution;
    const previousPeriod = table.options.meta.periodSelected.previousPeriod;
    const { formatDateTimeNoTz } = useFormatDateTime(timezone);

    return (
        <Tooltip
            content={
                <Trans
                    i18nKey={clsx({
                        'analytics:kpi.growth_tooltip.down': evolution < 0,
                        'analytics:kpi.growth_tooltip.up': evolution > 0,
                        'analytics:kpi.growth_tooltip.still': evolution === 0
                    })}
                    components={{ br: <br /> }}
                    values={{
                        current: formatData(data.value),
                        previous: formatData(data.previous),
                        start: formatDateTimeNoTz(previousPeriod.start, dateFormat),
                        end: formatDateTimeNoTz(previousPeriod.end, dateFormat)
                    }}
                />
            }>
            <Icon
                icon={clsx({
                    'arrow-bottom': evolution < 0,
                    'arrow-top': evolution > 0,
                    'arrow-right': evolution === 0
                })}
                className={clsx({
                    'text-icoRed': evolution < 0,
                    'text-icoGreen': evolution > 0,
                    'text-icoGray-400': evolution === 0
                })}
            />
        </Tooltip>
    );
};

export type KpiCellProps = {
    data: KPIDataType;
    isHighlight?: boolean;
    hasStats?: boolean;
    format?:
        | typeof KPI_VALUE_MEDIA_VIEWER_CLASSIC
        | typeof KPI_VALUE_CLASSIC
        | typeof KPI_VALUE_DATE
        | typeof KPI_VALUE_DAY_OF_WEEK
        | typeof KPI_VALUE_TIME
        | typeof KPI_VALUE_TIME_DECIMAL
        | typeof KPI_VALUE_PERCENT
        | typeof KPI_VALUE_MEDIA_VIEWER_PERCENT;
};

const KpiCell = ({
    data,
    format = KPI_VALUE_MEDIA_VIEWER_CLASSIC,
    isHighlight = false,
    hasStats = true
}: KpiCellProps) => {
    const { table } = useContext(CompoundTableContext);
    const numbersFormat = table.options.meta.numbers_format;
    const dateFormat = table.options.meta.date_format;
    const timeFormat = table.options.meta.time_format;

    const { formatKpiValue } = useFormatKpiValue(format, numbersFormat, dateFormat, timeFormat);
    const hasValue = data && 'value' in data && typeof data.value === 'number';

    // If data is undefined, it means that the data is being fetched
    if (!hasStats || data === undefined) {
        return (
            <span
                data-testid="kpi-cell-placeholder"
                className="h-3.5 w-17 animate-pulse rounded-sm bg-icoGray-300 dark:bg-icoDarkMode-deepBlack"
            />
        );
    }

    return (
        <span
            className={clsx('flex h-full w-full items-center justify-center space-x-2.5 text-xs', {
                'bg-icoGray-100 text-icoGray-400 dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf':
                    !hasValue
            })}>
            <span
                className={clsx({
                    'font-GMedium': !isHighlight,
                    'font-GBold': isHighlight
                })}>
                {hasValue ? formatKpiValue(data.value) : 'N/A'}
            </span>
            {hasValue && 'evolution' in data && typeof data.evolution === 'number' ? (
                <IconEvolution data={data} formatData={formatKpiValue} />
            ) : null}
        </span>
    );
};

export default KpiCell;
